import React, { useState } from 'react';
import { ActionIcon, Button, Group, Stack, Text, TextInput } from '@mantine/core';
import { IconPencil, IconTrash } from '@tabler/icons-react';
import type { PropEntry } from '../../data/ShellConfig';

type PropEditorProps = {
    props: PropEntry[];
    onChange: (props: PropEntry[]) => void;
};

/**
 * Custom Props key/value list editor (Add / Edit / Delete / Save per row).
 * Values are public configuration — never secrets.
 */
const PropEditor: React.FC<PropEditorProps> = ({ props, onChange }) => {
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [draftKey, setDraftKey] = useState('');
    const [draftValue, setDraftValue] = useState('');
    const [adding, setAdding] = useState(false);

    const startAdd = () => {
        setAdding(true);
        setEditingIndex(null);
        setDraftKey('');
        setDraftValue('');
    };

    const startEdit = (index: number) => {
        setAdding(false);
        setEditingIndex(index);
        setDraftKey(props[index].key);
        setDraftValue(props[index].value);
    };

    const cancel = () => {
        setAdding(false);
        setEditingIndex(null);
        setDraftKey('');
        setDraftValue('');
    };

    const save = () => {
        const key = draftKey.trim();
        if (!key) return;
        const entry = { key, value: draftValue };
        if (adding) {
            const existing = props.findIndex(p => p.key === key);
            if (existing >= 0) {
                const next = [...props];
                next[existing] = entry;
                onChange(next);
            } else {
                onChange([...props, entry]);
            }
        } else if (editingIndex !== null) {
            const next = [...props];
            next[editingIndex] = entry;
            onChange(next);
        }
        cancel();
    };

    const remove = (index: number) => {
        onChange(props.filter((_, i) => i !== index));
        if (editingIndex === index) cancel();
    };

    return (
        <Stack gap="sm">
            <Text size="sm" fw={600}>
                Custom Props
            </Text>
            <Text size="xs" c="dimmed">
                Public key/value pairs appended to the Component iframe URL. Never put secrets here.
            </Text>

            {props.map((prop, index) =>
                editingIndex === index ? (
                    <Group key={`edit-${index}`} align="flex-end" grow>
                        <TextInput
                            label="Key"
                            value={draftKey}
                            onChange={e => setDraftKey(e.currentTarget.value)}
                        />
                        <TextInput
                            label="Value"
                            value={draftValue}
                            onChange={e => setDraftValue(e.currentTarget.value)}
                        />
                        <Button onClick={save} disabled={!draftKey.trim()}>
                            Save
                        </Button>
                        <Button variant="default" onClick={cancel}>
                            Cancel
                        </Button>
                    </Group>
                ) : (
                    <Group key={`${prop.key}-${index}`} justify="space-between">
                        <Text size="sm" ff="monospace">
                            {prop.key}={prop.value}
                        </Text>
                        <Group gap={4}>
                            <ActionIcon variant="subtle" onClick={() => startEdit(index)} aria-label="Edit">
                                <IconPencil size={16} />
                            </ActionIcon>
                            <ActionIcon variant="subtle" color="red" onClick={() => remove(index)} aria-label="Delete">
                                <IconTrash size={16} />
                            </ActionIcon>
                        </Group>
                    </Group>
                )
            )}

            {adding ? (
                <Group align="flex-end" grow>
                    <TextInput
                        label="Key"
                        value={draftKey}
                        onChange={e => setDraftKey(e.currentTarget.value)}
                    />
                    <TextInput
                        label="Value"
                        value={draftValue}
                        onChange={e => setDraftValue(e.currentTarget.value)}
                    />
                    <Button onClick={save} disabled={!draftKey.trim()}>
                        Save
                    </Button>
                    <Button variant="default" onClick={cancel}>
                        Cancel
                    </Button>
                </Group>
            ) : (
                <Button variant="light" onClick={startAdd} style={{ alignSelf: 'flex-start' }}>
                    Add a value
                </Button>
            )}
        </Stack>
    );
};

export default PropEditor;
